/*
 * @Author: Zhushitao
 * @Date: 2021-07-21 15:14:17
 * @LastEditors: zhushitao
 * @LastEditTime: 2021-11-18 17:17:43
 * @Description: Queuing class
 */
import { IAppContext } from '@byted-cg/linkchat-core';
import { I18n } from '../utils/lang';

import { ERROR_TYPE } from '../constants/error';
import { EventCode } from '../constants/eventCode';
import { USER_IDENTITY } from '../constants/identity';
import store from '../store/cache';
import { onErrorSubject } from './subject';

export enum QueueStatus {
  Ready = 'Ready',
  Pending = 'Pending',
  End = 'End',
}
class Queue {
  queueTimeout: number = 0;
  cacheQueueNumber: number = 0;
  queueStatus = QueueStatus.Ready;

  run = (appContext: IAppContext) => {
    if (store.get('debug')) {
      console.debug('run queue', this.queueStatus, appContext.dialog);
    }
    if (this.queueStatus === QueueStatus.Pending) {
      return;
    }
    this.queueStatus = QueueStatus.Pending;
    this.startQueue(appContext, true);
    this.queueTimeout = window.setInterval(() => {
      this.startQueue(appContext);
    }, 5000);
  };

  startQueue = async (appContext: IAppContext, initRun?: boolean) => {
    try {
      const res = await appContext.utils.services?.apis?.getDialogQueue({
        businessLineId:
          appContext.userInfo?.businessLineId &&
          String(appContext.userInfo?.businessLineId),
        skillId: 0,
        userId: '0',
        secretUserId: decodeURIComponent(appContext.userInfo?.secretUserId),
        jarvisEventCode:
          appContext.ext?.dialogEventCode?.[EventCode.TransferEventCode],
      });

      if (this.queueStatus === QueueStatus.End) {
        return;
      }
      if (!res) {
        throw new Error(I18n.t('no_queued_data_return', {}, '无排队数据返回'));
      }

      // The queue timed out and ended the queue.
      if (res.result === 630004 || res.result === 630005) {
        throw new Error(
          res.description || I18n.t('queuing_timeout', {}, '排队超时'),
        );
      }

      const data = res.data;
      const waitingNumber = data?.waitingNumber || 0;
      const showWaitingNumber = data?.showWaitingNumber || false;

      if (!this.cacheQueueNumber || waitingNumber <= this.cacheQueueNumber) {
        this.cacheQueueNumber = waitingNumber;
        const ext = {
          ...appContext.ext,
          waitingNumber,
          showWaitingNumber,
        };
        if (initRun) {
          ext.inWaiting = true;
        }
        appContext.ext = ext;
      }
      // Queued successfully
      if (Number(data.toUserId) !== USER_IDENTITY.SYS) {
        this.cacheQueueNumber = 0;
        return;
      }
    } catch (e) {
      if (+(e as any)?.response?.status === 401) {
        this.stopQueue();
      }
      onErrorSubject.next({
        type: ERROR_TYPE.QUEUE_ERROR,
        error: e,
      });
    }
  };

  public stopQueue = () => {
    this.queueTimeout && clearInterval(this.queueTimeout);
    this.queueStatus = QueueStatus.End;
    this.cacheQueueNumber = 0;
  };

  public clearQueue = async (appContext: IAppContext) => {
    this.stopQueue();
    appContext.ext.waitingNumber = 0;
    appContext.ext.inWaiting = false;
    appContext.ext = {
      ...appContext.ext,
      waitingNumber: 0,
      inWaiting: false,
    };
    try {
      const result = await appContext.utils.services?.apis?.cancelDialogQueue({
        businessLineId: String(appContext.userInfo?.businessLineId),
        skillId: 0,
        userId: '0',
        secretUserId: decodeURIComponent(appContext.userInfo?.secretUserId),
      });
      return result;
    } catch (e) {
      onErrorSubject.next({
        type: ERROR_TYPE.CLEAR_QUEUE_ERROR,
        error: e,
      });
    }
  };
}

export default new Queue();
